// teamRatingCalculator.js
// Direct port of TeamRatingCalculator.swift.
// All chemistry rules and percentages are identical to the iOS app.

// ─── Base Rating ─────────────────────────────────────────────────────────────

export function calculateTeamRating(players) {
  if (!players.length) return 0;
  const avg = players.reduce((sum, p) => sum + p.overallRating, 0) / players.length;
  return Math.max(0, Math.min(999, Math.round(avg * 10)));
}

// ─── Chemistry Bonus Calculation ─────────────────────────────────────────────
// Same-team pair:    5%
// Same-team stack:   8% (3+ players from same city)
// Same-team + year: 15% (2+ players from same city AND same year)

export function calculateChemistryBonuses(players) {
  const bonuses = [];
  const byCity = {};

  players.forEach(p => {
    if (!byCity[p.city]) byCity[p.city] = [];
    byCity[p.city].push(p);
  });

  Object.entries(byCity).forEach(([city, cityPlayers]) => {
    if (cityPlayers.length < 2) return;

    // Same team + year (15%)
    const byYear = {};
    cityPlayers.forEach(p => {
      if (!byYear[p.year]) byYear[p.year] = [];
      byYear[p.year].push(p);
    });
    Object.entries(byYear).forEach(([year, yearPlayers]) => {
      if (yearPlayers.length >= 2) {
        bonuses.push({
          type: "sameTeamAndEra",
          description: `${city} ${year} Connection`,
          bonusPercentage: 15,
          playersInvolved: yearPlayers,
        });
      }
    });

    // Same team tiered (5% pair, 8% stack)
    const pct = cityPlayers.length >= 3 ? 8 : 5;
    bonuses.push({
      type: "sameTeam",
      description: `${city} Team Chemistry`,
      bonusPercentage: pct,
      playersInvolved: cityPlayers,
    });
  });

  return bonuses;
}

// ─── Apply Bonuses (highest-wins per player) ──────────────────────────────────
// Per-player bonus is floored (truncated) to match Swift's Int(...) conversion.
// Using Math.round here would round 13.5 → 14 vs iOS's 13, inflating web scores.

function applyChemistryBonuses(players, bonuses) {
  return players.map(player => {
    let highest = 0;
    bonuses.forEach(bonus => {
      if (bonus.playersInvolved.some(p => p.name === player.name)) {
        const amount = Math.floor(player.overallRating * (bonus.bonusPercentage / 100));
        highest = Math.max(highest, amount);
      }
    });
    return player.overallRating + highest;
  });
}

// ─── Team Rating with Chemistry ───────────────────────────────────────────────

export function calculateTeamRatingWithChemistry(players) {
  if (!players.length) return 0;
  const bonuses = calculateChemistryBonuses(players);
  const adjusted = applyChemistryBonuses(players, bonuses);
  const avg = adjusted.reduce((s, r) => s + r, 0) / adjusted.length;
  return Math.max(0, Math.min(999, Math.round(avg * 10)));
}

// ─── Effective Scaled Points per Bonus Row ────────────────────────────────────
// Matches effectiveScaledPoints() in TeamRatingCalculator.swift so displayed
// row values always add up to the total chemistry shown.
// Per-player bonus is floored (truncated) to match Swift's Int(...) conversion;
// the final scale uses Math.round to match Swift's Int(scaled.rounded()).

export function effectiveScaledPoints(bonus, allBonuses, totalPlayerCount) {
  if (!totalPlayerCount) return 0;
  const effective = bonus.playersInvolved.filter(player => {
    const maxPct = Math.max(
      ...allBonuses
        .filter(b => b.playersInvolved.some(p => p.name === player.name))
        .map(b => b.bonusPercentage)
    );
    return bonus.bonusPercentage >= maxPct;
  });
  const rawSum = effective.reduce((s, p) => s + Math.floor(p.overallRating * bonus.bonusPercentage / 100), 0);
  return Math.round((rawSum * 10) / totalPlayerCount);
}

// ─── Chemistry Breakdown (for UI display) ────────────────────────────────────

export function getChemistryBreakdown(players) {
  const bonuses = calculateChemistryBonuses(players);
  const baseRating = calculateTeamRating(players);
  const chemistryRating = calculateTeamRatingWithChemistry(players);
  const totalChemistryPoints = bonuses.reduce(
    (sum, b) => sum + effectiveScaledPoints(b, bonuses, players.length), 0
  );
  return { baseRating, chemistryRating, totalChemistryPoints, activeBonuses: bonuses };
}

// ─── Team Quality Label ───────────────────────────────────────────────────────

export function getTeamQualityDescription(rating) {
  if (rating >= 950) return "LEGENDARY";
  if (rating >= 900) return "ELITE";
  if (rating >= 850) return "GREAT";
  if (rating >= 800) return "GOOD";
  if (rating >= 750) return "SOLID";
  if (rating >= 700) return "AVERAGE";
  return "NEEDS WORK";
}
